import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { DAYS_OF_WEEK } from '../lib/constants'
import { Modal, Grid, FormField, btnStyle, btnSecStyle } from './CardsPage'

export default function SchedulePage() {
  const { club } = useAuth()
  const [classes, setClasses] = useState([])
  const [categories, setCategories] = useState([])
  const [techniques, setTechniques] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [editClass, setEditClass] = useState(null)
  const [defaultDay, setDefaultDay] = useState('Monday')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!club) return
    const [cls, cats, techs] = await Promise.all([
      supabase.from('classes').select('*, class_techniques(*, techniques(*))').eq('club_id', club.id),
      supabase.from('skill_categories').select('*').eq('club_id', club.id).order('display_order'),
      supabase.from('techniques').select('*').order('display_order'),
    ])
    setClasses(cls.data || [])
    setCategories(cats.data || [])
    setTechniques(techs.data || [])
    setLoading(false)
  }, [club])

  useEffect(() => { load() }, [load])

  async function deleteClass(id) {
    if (!window.confirm('Delete this class?')) return
    await supabase.from('classes').delete().eq('id', id)
    load()
  }

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button onClick={() => { setEditClass(null); setDefaultDay('Monday'); setShowAdd(true) }} style={btnStyle}>
          + Add class
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {DAYS_OF_WEEK.map(day => {
          const dayClasses = classes.filter(c => c.day_of_week === day)
          return (
            <div key={day} style={{ background: '#f8f9fc', borderRadius: 10, padding: '10px 8px', minHeight: 100 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', marginBottom: 8 }}>
                {day.slice(0, 3)}
              </div>
              {dayClasses.map(cls => {
                const techs = cls.class_techniques?.map(ct => ct.techniques?.name).filter(Boolean) || []
                return (
                  <div key={cls.id}
                    onClick={() => { setEditClass(cls); setShowAdd(true) }}
                    style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 7, padding: '6px 7px', marginBottom: 5, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ fontSize: 9, color: '#888' }}>{cls.time_of_day} · {cls.class_type}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>{cls.name}</div>
                    {techs.length > 0 && <div style={{ fontSize: 9, color: '#1860a8', marginTop: 2 }}>{techs.slice(0, 2).join(', ')}{techs.length > 2 ? '…' : ''}</div>}
                    {cls.recurring && <div style={{ fontSize: 8, color: '#aaa', marginTop: 1 }}>recurring</div>}
                  </div>
                )
              })}
              <button
                onClick={() => { setEditClass(null); setDefaultDay(day); setShowAdd(true) }}
                style={{ width: '100%', background: 'transparent', border: '1px dashed #ccc', borderRadius: 6, padding: '4px', fontSize: 11, cursor: 'pointer', color: '#aaa' }}>
                +
              </button>
            </div>
          )
        })}
      </div>

      {showAdd && (
        <ClassModal
          clubId={club.id}
          defaultDay={defaultDay}
          editClass={editClass}
          categories={categories}
          techniques={techniques}
          onClose={() => { setShowAdd(false); setEditClass(null) }}
          onSaved={() => { setShowAdd(false); setEditClass(null); load() }}
          onDelete={deleteClass}
        />
      )}
    </div>
  )
}

function ClassModal({ clubId, defaultDay, editClass, categories, techniques, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState({
    name: editClass?.name || '',
    day_of_week: editClass?.day_of_week || defaultDay,
    time_of_day: editClass?.time_of_day || '',
    class_type: editClass?.class_type || 'kids',
    recurring: editClass?.recurring ?? true,
  })
  const [selectedTechs, setSelectedTechs] = useState(
    editClass?.class_techniques?.map(ct => ct.technique_id) || []
  )
  const [selCat, setSelCat] = useState(categories[0]?.id || '')
  const [saving, setSaving] = useState(false)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const catTechs = techniques.filter(t => t.category_id === selCat)

  function toggleTech(id) {
    setSelectedTechs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  async function save() {
    setSaving(true)
    let classId = editClass?.id
    if (editClass) {
      await supabase.from('classes').update({ ...form, club_id: clubId }).eq('id', classId)
      await supabase.from('class_techniques').delete().eq('class_id', classId)
    } else {
      const { data } = await supabase.from('classes').insert({ ...form, club_id: clubId }).select().single()
      classId = data?.id
    }
    if (classId && selectedTechs.length) {
      await supabase.from('class_techniques').insert(selectedTechs.map(tid => ({ class_id: classId, technique_id: tid })))
    }
    setSaving(false)
    onSaved()
  }

  return (
    <Modal title={editClass ? 'Edit class' : 'Add class'} onClose={onClose}>
      <Grid>
        <FormField label="Class name"><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Kids BJJ" /></FormField>
        <FormField label="Day">
          <select value={form.day_of_week} onChange={e => set('day_of_week', e.target.value)}>
            {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <option key={d}>{d}</option>)}
          </select>
        </FormField>
        <FormField label="Time"><input value={form.time_of_day} onChange={e => set('time_of_day', e.target.value)} placeholder="18:00" /></FormField>
        <FormField label="Type">
          <select value={form.class_type} onChange={e => set('class_type', e.target.value)}>
            <option value="kids">Kids</option><option value="adult">Adults</option><option value="all">All</option>
          </select>
        </FormField>
      </Grid>
      <FormField label="Techniques taught today" fullWidth>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <select value={selCat} onChange={e => setSelCat(e.target.value)} style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {catTechs.map(t => {
            const sel = selectedTechs.includes(t.id)
            return (
              <button key={t.id} onClick={() => toggleTech(t.id)}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: '1px solid', fontFamily: 'inherit',
                  background: sel ? '#1860a8' : '#fff', color: sel ? '#fff' : '#444', borderColor: sel ? '#1860a8' : '#ddd' }}>
                {t.name}
              </button>
            )
          })}
        </div>
        {selectedTechs.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#1860a8' }}>
            Selected: {techniques.filter(t => selectedTechs.includes(t.id)).map(t => t.name).join(', ')}
          </div>
        )}
      </FormField>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
        <input type="checkbox" id="recurring" checked={form.recurring} onChange={e => set('recurring', e.target.checked)} />
        <label htmlFor="recurring" style={{ fontSize: 13, cursor: 'pointer' }}>Recurring weekly</label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={btnStyle}>{saving ? 'Saving...' : 'Save class'}</button>
        <button onClick={onClose} style={btnSecStyle}>Cancel</button>
        {editClass && <button onClick={() => { onClose(); onDelete(editClass.id) }} style={{ ...btnSecStyle, color: '#c00', borderColor: '#fcc', marginLeft: 'auto' }}>Delete</button>}
      </div>
    </Modal>
  )
}
